import { useCallback } from 'react';
import sendExperienceAnalytics from '@atlassian/jira-common-experience-tracking-analytics/src/index.tsx';
import { DUPLICATE_GADGET_EXPERIENCE } from '@atlassian/jira-dashboard-common/src/constants.tsx';
import { mapToReactGadgets } from '@atlassian/jira-dashboard-common/src/utils/map-to-react-gadgets.tsx';
import { useOnAddedGadget } from '@atlassian/jira-dashboard-internal-common/src/controllers/dashboard/context.tsx';
import { lensGadgetsList } from '@atlassian/jira-dashboard-internal-common/src/utils/dashboard-config-lenses/index.tsx';
import { handleFetchErrorAsExperience } from '@atlassian/jira-dashboard-internal-common/src/utils/handle-fetch-error-as-experience/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useFlagsService, type FlagConfiguration } from '@atlassian/jira-flags';
import { toFlagId } from '@atlassian/jira-flags/src/services/types';
import { useDashboardResource } from '@atlassian/jira-router-resources-dashboard/src/index.tsx';
import {
	PACKAGE_NAME,
	IGNORED_GADGETS_FROM_COUNT_MAP,
	MAX_ALLOWED_GADGETS_COUNT,
	MAX_GADGETS_COUNT_INCLUDE_IGNORED_GADGETS,
} from '../../constants.tsx';
import { useAddGadgetWithinLimit } from '../../utils/add-gadget-within-limit/index.tsx';
import messages from './messages.tsx';

export const expAttributes = {
	analyticsSource: 'dashboard',
	experience: DUPLICATE_GADGET_EXPERIENCE,
	application: null,
	edition: null,
	additionalAttributes: {},
} as const;

const errorFlagAttributes: FlagConfiguration = {
	key: toFlagId('gadgetDuplicateError'),
	type: 'error',
	title: messages.errorFlagTitle,
	description: messages.errorFlagDescription,
	messageId: 'dashboard-edit.services.duplicate-gadget.error-flag-attributes.error',
	messageType: 'transactional',

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	actions: [{ content: messages.errorFlagAction, onClick: () => window.location.reload() }],
};

const successFlagAttributes = (name: string): FlagConfiguration => ({
	key: toFlagId('gadgetDuplicateSuccess'),
	type: 'success',
	title: messages.successFlagTitle,
	description: [messages.successFlagDescription, { name }],
	messageId: 'dashboard-edit.services.duplicate-gadget.flag.success.success-flag-attributes',
	messageType: 'transactional',
});

export const useDuplicateGadget = (id: string) => {
	const { data, update } = useDashboardResource();
	const { canAddGadgetWithinLimit, showGadgetLimitFlag } = useAddGadgetWithinLimit(
		MAX_GADGETS_COUNT_INCLUDE_IGNORED_GADGETS,
		MAX_ALLOWED_GADGETS_COUNT,
	);
	const { showFlag } = useFlagsService();
	const onBeforeGadgetAdded = useOnAddedGadget();

	return useCallback(async () => {
		if (!data) {
			return;
		}

		const originalGadget = data.gadgets.find((gadget) => gadget.id === id);
		if (!originalGadget) {
			showFlag(errorFlagAttributes);
			return;
		}

		const isGadgetIgnored =
			!!originalGadget?.amdModule &&
			Object.values<string>(IGNORED_GADGETS_FROM_COUNT_MAP).includes(originalGadget.amdModule);

		if (!canAddGadgetWithinLimit(data.gadgets, isGadgetIgnored)) {
			showGadgetLimitFlag();
			return;
		}

		const duplicateGadgetUrl = `/rest/dashboards/1.0/${data.id}/gadget/${id}/duplicate`;

		performPostRequest(duplicateGadgetUrl)
			.then((gadgetData) => {
				onBeforeGadgetAdded(gadgetData.id);
				update(lensGadgetsList((gadgets) => [...mapToReactGadgets([gadgetData]), ...gadgets]));
				showFlag(successFlagAttributes(originalGadget.title));
				sendExperienceAnalytics({
					...expAttributes,
					wasExperienceSuccesful: true,
				});
			})
			.catch((error) => {
				showFlag(errorFlagAttributes);

				handleFetchErrorAsExperience({
					error,
					sendSuccessExperience: () => {
						sendExperienceAnalytics({
							...expAttributes,
							wasExperienceSuccesful: true,
						});
					},
					sendFailedExperience: () => {
						sendExperienceAnalytics({
							...expAttributes,
							wasExperienceSuccesful: false,
						});
					},
					logErrorEvent: (reason) => {
						fireErrorAnalytics({
							error,
							meta: {
								id: DUPLICATE_GADGET_EXPERIENCE,
								packageName: PACKAGE_NAME,
							},
							attributes: {
								reason,
								endpoint: 'duplicateGadget',
							},
						});
					},
				});
			});
	}, [
		data,
		canAddGadgetWithinLimit,
		id,
		showFlag,
		showGadgetLimitFlag,
		onBeforeGadgetAdded,
		update,
	]);
};
